/*
 * tss-bundle-engine.js — Bundle Builder UI, 9 Oct 2026. State + DOM only;
 * money math is in tss-bundle-pricing.js (load first). A3 is chosen per slot.
 * Accepts ?tier=, ?seed=, ?extra=. The computed price is advisory until it
 * matches the carrier variant's real price; a mismatch disables checkout.
 */
(() => {
  class TssBundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === 'true') return;
      const model = this.readJson('#tss-pricing-data', document);
      if (!model || !window.TSSBundlePricing) return;

      this.dataset.bound = 'true';
      this.p = window.TSSBundlePricing.create(model);
      this.carrier = this.readJson('[data-tss-carrier]', this);
      this.maxBooks = Number(this.dataset.maxBooks || 4);
      this.minBooks = Number(this.dataset.minBooks || 2);

      this.state = { tier: 3, books: [], extra: 'none', scent: 'Lavender' };

      this.cacheNodes();
      this.bindEvents();
      this.bindPreview();
      this.applyQuery();
      this.render();
    }

    readJson(sel, scope) {
      const n = scope.querySelector(sel);
      if (!n) return null;
      try {
        return JSON.parse(n.textContent);
      } catch (e) {
        return null;
      }
    }

    cacheNodes() {
      const q = (s) => this.querySelector(s);
      const all = (s) => [...this.querySelectorAll(s)];
      this.nodes = {
        tiles: all('[data-tss-tier]'),
        counter: q('[data-tss-counter]'),
        slots: q('[data-tss-slots]'),
        books: all('[data-tss-book]'),
        rails: all('[data-tss-rail]'),
        search: q('[data-tss-search]'),
        searchEmpty: q('[data-tss-search-empty]'),
        extras: all('[data-tss-extra]'),
        scentWrap: q('[data-tss-scent]'),
        scentSelect: q('[data-tss-scent-select]'),
        summaryList: q('[data-tss-summary-list]'),
        total: q('[data-tss-total]'),
        compareRow: q('[data-tss-compare-row]'),
        compare: q('[data-tss-compare]'),
        save: q('[data-tss-save]'),
        nudge: q('[data-tss-nudge]'),
        add: q('[data-tss-add]'),
        error: q('[data-tss-error]'),
      };
    }

    bindPreview() {
      const dialog=this.querySelector('[data-tss-preview-dialog]');
      if(!dialog)return;
      this.previewDialog=dialog;
      const q=s=>dialog.querySelector(s);
      this.nodes.books.forEach(card=>{
        const button=card.querySelector('[data-tss-preview]');
        if(button)button.addEventListener('click',()=>{
          this.previewCard=card;
          this.previewOpener=button;
          this.previewPhotos=this.readJson('[data-tss-gallery]',card)||[];
          this.previewIndex=0;
          q('[data-preview-title]').textContent=card.dataset.title;
          const thumbs=q('[data-preview-thumbs]');
          thumbs.replaceChildren();
          this.previewPhotos.forEach((photo,index)=>{
            const b=document.createElement('button');
            b.type='button';b.setAttribute('aria-label','View photo '+(index+1));
            const img=document.createElement('img');img.src=photo.src;img.alt='';img.loading='lazy';b.append(img);
            b.addEventListener('click',()=>{this.previewIndex=index;this.renderPreview();});
            thumbs.append(b);
          });
          this.renderPreview();
          this.previewBodyOverflow=document.body.style.overflow;
          document.body.style.overflow='hidden';
          dialog.showModal();
        });
      });
      q('[data-preview-close]').addEventListener('click',()=>dialog.close());
      dialog.addEventListener('click',event=>{if(event.target===dialog){const r=dialog.getBoundingClientRect();if(event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom)dialog.close();}});
      dialog.addEventListener('close',()=>{document.body.style.overflow=this.previewBodyOverflow||'';this.previewOpener?.focus({preventScroll:true});});
      const move=delta=>{if(this.previewPhotos.length){this.previewIndex=(this.previewIndex+delta+this.previewPhotos.length)%this.previewPhotos.length;this.renderPreview();}};
      q('[data-preview-prev]').addEventListener('click',()=>move(-1));
      q('[data-preview-next]').addEventListener('click',()=>move(1));
      dialog.addEventListener('keydown',event=>{if(event.key==='ArrowLeft'){event.preventDefault();move(-1);}if(event.key==='ArrowRight'){event.preventDefault();move(1);}});
      let touchX=0;
      q('[data-preview-image]').addEventListener('touchstart',e=>{touchX=e.changedTouches[0].clientX;},{passive:true});
      q('[data-preview-image]').addEventListener('touchend',e=>{const dx=e.changedTouches[0].clientX-touchX;if(Math.abs(dx)>50)move(dx<0?1:-1);},{passive:true});
      q('[data-preview-add]').addEventListener('click',()=>{
        const card=this.previewCard;
        const selected=this.state.books.some(b=>b.handle===card.dataset.handle);
        if(!selected&&this.state.books.length>=this.state.tier)return;
        this.toggle(card.dataset.handle,card.dataset.title);
        this.renderPreview();
      });
    }

    renderPreview() {
      const dialog=this.previewDialog;
      if(!dialog||!this.previewCard)return;
      const q=s=>dialog.querySelector(s);
      const photo=this.previewPhotos[this.previewIndex];
      q('[data-preview-image]').hidden=!photo;
      q('[data-preview-empty]').hidden=!!photo;
      if(photo){q('[data-preview-image]').src=photo.src;q('[data-preview-image]').alt=photo.alt||this.previewCard.dataset.title;}
      q('[data-preview-position]').textContent=photo?'Photo '+(this.previewIndex+1)+' of '+this.previewPhotos.length:'No photos';
      q('[data-preview-prev]').disabled=this.previewPhotos.length<2;
      q('[data-preview-next]').disabled=this.previewPhotos.length<2;
      [...q('[data-preview-thumbs]').children].forEach((b,i)=>b.setAttribute('aria-pressed',String(i===this.previewIndex)));
      const selected=this.state.books.some(b=>b.handle===this.previewCard.dataset.handle);
      const full=this.state.books.length>=this.state.tier;
      q('[data-preview-counter]').textContent=this.state.books.length+' of '+this.state.tier+' books selected';
      q('[data-preview-add]').textContent=selected?'Remove from bundle':full?'Bundle full — remove a book first':'Add this book';
      q('[data-preview-add]').disabled=!selected&&full;
    }

    bindEvents() {
      this.nodes.tiles.forEach((t) =>
        t.addEventListener('click', () => this.setTier(Number(t.dataset.tssTier)))
      );

      this.nodes.books.forEach((card) => {
        const pick = card.querySelector('[data-tss-pick]');
        if (pick) pick.addEventListener('click', () => this.toggle(card.dataset.handle, card.dataset.title));
      });

      if (this.nodes.search) {
        this.nodes.search.addEventListener('input', (e) => this.filter(e.target.value));
        // type=search fires `search` on the native clear (x), not `input`.
        this.nodes.search.addEventListener('search', (e) => this.filter(e.target.value));
      }

      this.nodes.extras.forEach((b) =>
        b.addEventListener('click', () => {
          if (b.disabled) return;
          this.state.extra = b.dataset.tssExtra;
          this.render();
        })
      );

      if (this.nodes.scentSelect) {
        this.nodes.scentSelect.addEventListener('change', (e) => {
          this.state.scent = e.target.value;
          this.render();
        });
      }

      if (this.nodes.add) this.nodes.add.addEventListener('click', () => this.addToCart());
    }

    applyQuery() {
      const q = new URLSearchParams(window.location.search);

      const tier = Number(q.get('tier'));
      if (tier >= this.minBooks && tier <= this.maxBooks) this.state.tier = tier;

      const extra = q.get('extra');
      if (extra && ['none', 'kit', 'candle', 'both'].indexOf(extra) > -1) this.state.extra = extra;

      const seed = q.get('seed');
      if (seed) {
        const card = this.nodes.books.find((c) => c.dataset.handle === seed);
        if (card) this.toggle(card.dataset.handle, card.dataset.title, true);
      }
    }

    setTier(n) {
      if (!n || n === this.state.tier) return;
      this.state.tier = n;
      if (this.state.books.length > n) this.state.books = this.state.books.slice(-n);
      this.render();
    }

    toggle(handle, title, silent) {
      const i = this.state.books.findIndex((b) => b.handle === handle);
      if (i > -1) {
        this.state.books.splice(i, 1);
      } else {
        if (this.state.books.length >= this.state.tier) {
          if (this.state.tier < this.maxBooks) this.state.tier += 1;
          else this.state.books.shift();
        }
        this.state.books.push({ handle: handle, title: title, size: 'a4' });
      }
      if (!silent) this.render();
    }

    setSize(handle, size) {
      const b = this.state.books.find((x) => x.handle === handle);
      if (!b) return;
      b.size = size;
      this.render();
    }

    // Needs `.tss-book[hidden] { display: none !important }` in tss-bundle.css:
    // the UA [hidden] rule loses to `.tss-book { display: flex }`, which is why
    // search silently did nothing before.
    filter(term) {
      const query = (term || '').trim().toLowerCase();
      const seen = {};

      this.nodes.books.forEach((card) => {
        let show = !query || (card.dataset.search || '').indexOf(query) > -1;

        // Show each book once while searching: a title in three collections
        // otherwise returns three identical cards.
        if (show && query) {
          if (seen[card.dataset.handle]) show = false;
          else seen[card.dataset.handle] = true;
        }

        card.hidden = !show;
      });

      let visibleRails = 0;
      this.nodes.rails.forEach((rail) => {
        const any = [...rail.querySelectorAll('[data-tss-book]')].some((c) => !c.hidden);
        rail.hidden = !any;
        if (any) visibleRails += 1;
      });

      if (this.nodes.searchEmpty) this.nodes.searchEmpty.hidden = !(query && visibleRails === 0);
    }

    render() {
      const n = this.state.tier;

      this.nodes.tiles.forEach((t) => {
        const on = Number(t.dataset.tssTier) === n;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-checked', String(on));
      });

      if (this.nodes.counter) {
        this.nodes.counter.textContent = this.state.books.length + ' of ' + n + ' selected';
        this.nodes.counter.classList.toggle('is-complete', this.state.books.length === n);
      }

      this.nodes.books.forEach((card) => {
        const on = this.state.books.some((b) => b.handle === card.dataset.handle);
        card.classList.toggle('is-selected', on);
        const pick = card.querySelector('[data-tss-pick]');
        if (pick) pick.setAttribute('aria-pressed', String(on));
        const label=card.querySelector('[data-tss-add-label]');
        if(label)label.textContent=on?'Added ✓':'Add book';
      });

      this.renderSlots();

      this.nodes.extras.forEach((b) => {
        const locked = n < Number(b.dataset.tssMinBooks || 1);
        b.disabled = locked;
        b.classList.toggle('is-locked', locked);
        const on = b.dataset.tssExtra === this.state.extra;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-checked', String(on));
        const node = b.querySelector('[data-tss-extra-price]');
        if (node) {
          const up = this.p.extrasUplift(n, b.dataset.tssExtra);
          node.textContent = up > 0 ? '+ ' + this.p.money(up) : 'Included';
        }
      });

      const wantsCandle = this.state.extra === 'candle' || this.state.extra === 'both';
      if (this.nodes.scentWrap) this.nodes.scentWrap.hidden = !wantsCandle;

      this.renderSummary();
      this.renderTotals();
      this.renderButton();
    }

    // A filled slot is where A4/A3 is chosen.
    renderSlots() {
      const wrap = this.nodes.slots;
      if (!wrap) return;
      wrap.innerHTML = '';

      for (let i = 0; i < this.state.tier; i += 1) {
        const book = this.state.books[i];
        const slot = document.createElement('div');
        slot.className = 'tss-slot' + (book ? ' is-filled' : '');

        if (!book) {
          slot.textContent = i + 1 + '. Choose a book';
          wrap.appendChild(slot);
          continue;
        }

        const name = document.createElement('span');
        name.className = 'tss-slot__name';
        name.textContent = i + 1 + '. ' + book.title;

        const row = document.createElement('div');
        row.className = 'tss-slot__row';

        const sizes = document.createElement('div');
        sizes.className = 'tss-slot__sizes';
        sizes.setAttribute('role', 'radiogroup');
        ['a4', 'a3'].forEach((size) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'tss-slot__size' + (book.size === size ? ' is-active' : '');
          btn.setAttribute('role', 'radio');
          btn.setAttribute('aria-checked', String(book.size === size));
          btn.textContent = size.toUpperCase();
          btn.addEventListener('click', () => this.setSize(book.handle, size));
          sizes.appendChild(btn);
        });

        const clear = document.createElement('button');
        clear.type = 'button';
        clear.className = 'tss-slot__clear';
        clear.setAttribute('aria-label', 'Remove ' + book.title);
        clear.textContent = '×';
        clear.addEventListener('click', () => this.toggle(book.handle, book.title));

        row.appendChild(sizes);
        row.appendChild(clear);
        slot.appendChild(name);
        slot.appendChild(row);
        wrap.appendChild(slot);
      }
    }

    renderSummary() {
      const list = this.nodes.summaryList;
      if (!list) return;
      list.innerHTML = '';

      for (let i = 0; i < this.state.tier; i += 1) {
        const book = this.state.books[i];
        const li = document.createElement('li');
        li.className = 'tss-summary__item' + (book ? '' : ' tss-summary__item--empty');
        if (book) {
          li.innerHTML =
            '<span>' + this.escape(book.title) + '</span>' +
            '<span class="tss-summary__item-meta">' + (book.size === 'a3' ? 'A3' : 'A4') + '</span>';
        } else {
          li.innerHTML = '<span>Book ' + (i + 1) + ' &mdash; not chosen</span>';
        }
        list.appendChild(li);
      }

      if (this.state.extra !== 'none') {
        const li = document.createElement('li');
        li.className = 'tss-summary__item tss-summary__item--extra';
        const scent =
          this.state.extra === 'candle' || this.state.extra === 'both'
            ? ' · ' + this.escape(this.state.scent)
            : '';
        li.innerHTML =
          '<span>' + this.escape(this.p.label(this.state.extra)) + '</span>' +
          '<span class="tss-summary__item-meta">+ ' +
          this.p.money(this.p.extrasUplift(this.state.tier, this.state.extra)) + scent + '</span>';
        list.appendChild(li);
      }
    }

    renderTotals() {
      const price = this.p.price(this.state);
      const compare = this.p.compare(this.state);
      const saving = compare - price;

      if (this.nodes.total) this.nodes.total.textContent = this.p.money(price);
      if (this.nodes.compareRow) this.nodes.compareRow.hidden = saving <= 0;
      if (this.nodes.compare) this.nodes.compare.textContent = this.p.money(compare);
      if (this.nodes.save) {
        this.nodes.save.hidden = saving <= 0;
        this.nodes.save.textContent = 'You save ' + this.p.money(saving);
      }

      if (this.nodes.nudge) {
        const next = this.state.tier + 1;
        if (next <= this.maxBooks) {
          const now = this.p.baseFor(this.state.tier);
          const then = this.p.baseFor(next);
          this.nodes.nudge.hidden = false;
          this.nodes.nudge.innerHTML =
            'Add a ' + this.ordinal(next) + ' book for just <strong>' + this.p.money(then - now) +
            '</strong> &mdash; every book drops to ' + this.p.money(this.p.perBook(next)) + '.';
        } else {
          this.nodes.nudge.hidden = true;
        }
      }
    }

    renderButton() {
      const btn = this.nodes.add;
      const err = this.nodes.error;
      if (!btn) return;

      // Cleared every pass so a stale id can never reach the cart.
      this.readyVariantId = null;

      const picked = this.state.books.length;
      const fail = (msg) => {
        btn.disabled = true;
        btn.textContent = 'Unavailable';
        if (err) {
          err.hidden = false;
          err.textContent = msg;
        }
      };

      if (picked !== this.state.tier) {
        btn.disabled = true;
        const left = this.state.tier - picked;
        btn.textContent = left > 0 ? 'Choose ' + left + ' more book' + (left === 1 ? '' : 's') : 'Add bundle to cart';
        if (err) err.hidden = true;
        return;
      }

      if (this.dataset.carrierReady !== 'true' || !this.carrier) {
        fail('Bundle carrier product is not connected yet, so checkout is disabled.');
        return;
      }

      const variant = this.p.carrierVariant(this.carrier, this.state);
      if (!variant) {
        fail('No carrier variant for ' + this.state.tier + ' books / ' +
          this.p.a3Count(this.state.books) + ' A3 / ' + this.p.EXTRA_TO_CARRIER[this.state.extra] + '.');
        return;
      }

      const expected = this.p.price(this.state);
      if (Number(variant.price) !== expected) {
        fail('Price mismatch: should be ' + this.p.money(expected) + ' but the carrier variant is ' +
          this.p.money(variant.price) + '. Fix the carrier price before selling.');
        return;
      }

      if (variant.available === false) {
        fail('This combination is currently unavailable.');
        return;
      }

      btn.disabled = false;
      btn.textContent = 'Add to cart · ' + this.p.money(expected);
      if (err) err.hidden = true;
      this.readyVariantId = variant.id;
    }

    lineItemProperties() {
      const props = {
        Books: this.state.books
          .map((b, i) => i + 1 + '. ' + b.title + ' (' + (b.size === 'a3' ? 'Jumbo A3' : 'Standard A4') + ')')
          .join('\n'),
        Extras: this.p.label(this.state.extra),
        _bundle_tier: String(this.state.tier),
        _bundle_a3: String(this.p.a3Count(this.state.books)),
        _bundle_handles: this.state.books.map((b) => b.handle).join(','),
      };
      if (this.state.extra === 'candle' || this.state.extra === 'both') {
        props['Candle scent'] = this.state.scent;
      }
      return props;
    }

    async addToCart() {
      const btn = this.nodes.add;
      const err = this.nodes.error;
      if (!this.readyVariantId) return;

      const original = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding…';

      try {
        if (!window.TSSCheckout) throw new Error('Secure checkout is still loading. Please try again.');
        await window.TSSCheckout.addLines([
          { id: this.readyVariantId, quantity: 1, properties: this.lineItemProperties() },
        ]);
        btn.disabled = false;
        btn.textContent = original;
      } catch (e) {
        btn.disabled = false;
        btn.textContent = original;
        if (err) {
          err.hidden = false;
          err.textContent = e.message || 'Could not add to cart.';
        }
      }
    }

    escape(str) {
      const d = document.createElement('div');
      d.textContent = String(str == null ? '' : str);
      return d.innerHTML;
    }

    ordinal(n) {
      return { 1: '1st', 2: '2nd', 3: '3rd', 4: '4th' }[n] || n + 'th';
    }
  }

  if (!customElements.get('tss-bundle-builder')) {
    customElements.define('tss-bundle-builder', TssBundleBuilder);
  }
})();

